import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Settings } from 'types/general.types';
import { ThemeMode } from 'types/enums';
import { getWidth } from 'app/utils/hooks/useResizableBlock/utils';
import { Collaborator } from 'types/collaborator.types';
import { fetchSettingsEffect, updateBadgeVisibleEffect } from './effects';

export interface SettingsState {
  paletteType: ThemeMode;
  themeMode: ThemeMode;
  settings: Settings;
  scrollIsMounted: boolean;
  showMobileNotification: boolean;
  sidebar: {
    width: string;
  };
  coWriterBar?: {
    type: 'rhymes' | 'synonyms' | 'alliteration' | 'word-chain';
    lyrics?: string;
  };
  profileBar?: {
    collaborator: Pick<Collaborator, 'profile' | 'profileCard'>;
  };
  songChatBar?: {};
}

let themeMode = localStorage.getItem('theme') as ThemeMode;
if (!themeMode) {
  themeMode = ThemeMode.SYSTEM;
  localStorage.setItem('theme', themeMode);
  localStorage.removeItem('chakra-ui-color-mode');
}

export const settingsInitialState: SettingsState = {
  themeMode,
  paletteType: (localStorage.getItem('theme') ||
    localStorage.getItem('chakra-ui-color-mode')) as ThemeMode,
  settings: {
    badge: {
      visible: true,
    },
  },
  scrollIsMounted: false,
  showMobileNotification: true,
  sidebar: {
    width: getWidth('sidebar'),
  },
};

const slice = createSlice({
  name: 'settings',
  initialState: settingsInitialState,
  reducers: {
    setThemeModeAction(state, { payload }: PayloadAction<ThemeMode>) {
      state.paletteType = payload;
      state.themeMode = payload;
      localStorage.setItem('theme', payload);
    },
    setThemePaletteAction(state, { payload }: PayloadAction<ThemeMode>) {
      state.paletteType = payload;
    },
    setScrollIsMounted(state, { payload }: PayloadAction<boolean>) {
      state.scrollIsMounted = payload;
    },
    setShowMobileNotification(state, { payload }: PayloadAction<boolean>) {
      state.showMobileNotification = payload;
    },
    setSettings(state, { payload }: PayloadAction<Settings>) {
      state.settings = payload;
    },
    setSidebarSettings(
      state,
      { payload }: PayloadAction<SettingsState['sidebar']>
    ) {
      state.sidebar = payload;
    },
    setCoWriterBarSettings(
      state,
      { payload }: PayloadAction<SettingsState['coWriterBar']>
    ) {
      if (payload) {
        state.profileBar = undefined;
        state.songChatBar = undefined;
      }
      state.coWriterBar = payload;
    },
    setProfileBarSettings(
      state,
      { payload }: PayloadAction<SettingsState['profileBar']>
    ) {
      state.coWriterBar = undefined;
      state.songChatBar = undefined;
      state.profileBar = payload;
    },
    setSongChatBarSettings(
      state,
      { payload }: PayloadAction<{ songId: string } | undefined>
    ) {
      state.coWriterBar = undefined;
      state.profileBar = undefined;
      state.songChatBar = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettingsEffect.fulfilled, (state, { payload }) => {
        state.settings = payload;
      })
      .addCase(updateBadgeVisibleEffect.fulfilled, (state, { meta }) => {
        state.settings.badge.visible = meta.arg.visible;
      });
  },
});

export const {
  setThemeModeAction,
  setThemePaletteAction,
  setScrollIsMounted,
  setShowMobileNotification,
  setSettings,
  setSidebarSettings,
  setCoWriterBarSettings,
  setProfileBarSettings,
  setSongChatBarSettings,
} = slice.actions;

export const settingsReducer = slice.reducer;
